import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

interface UiState {
  sidebarCollapsed: boolean;
  mobileSidebarOpen: boolean;
  colorMode: 'light' | 'dark';
}

const STORAGE_KEY = 'otrack.ui';

function loadPersisted(): UiState {
  const fallback: UiState = {
    sidebarCollapsed: false,
    mobileSidebarOpen: false,
    colorMode: 'light',
  };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<UiState>;
    // mobileSidebarOpen is never persisted — always start closed.
    return { ...fallback, ...parsed, mobileSidebarOpen: false };
  } catch {
    return fallback;
  }
}

function persist(state: UiState): void {
  try {
    const { mobileSidebarOpen: _, ...saveable } = state;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(saveable));
  } catch {
    // preference not saved
  }
}

const uiSlice = createSlice({
  name: 'ui',
  initialState: loadPersisted(),
  reducers: {
    toggleSidebar(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed;
      persist(state);
    },
    setMobileSidebarOpen(state, action: PayloadAction<boolean>) {
      state.mobileSidebarOpen = action.payload;
    },
    setColorMode(state, action: PayloadAction<UiState['colorMode']>) {
      state.colorMode = action.payload;
      persist(state);
    },
  },
});

export const { toggleSidebar, setMobileSidebarOpen, setColorMode } = uiSlice.actions;
export const uiReducer = uiSlice.reducer;
